export const BLOCKS = {
  foundation: {
    id: 'foundation',
    name: 'Foundation',
    code: 'F',
    number: 1,
    color: '#9B59B6',
    lightColor: '#E8D5F0',
    description: 'Mathematical & Theoretical Foundations',
    longDescription: 'The atomic building blocks of AI. These are the fundamentals: mathematical concepts, optimization techniques, and core mechanisms that everything else depends on. Without understanding Foundation, you cannot truly understand any AI system.',
    icon: '∑',
  },
  architecture: {
    id: 'architecture',
    name: 'Architecture',
    code: 'A',
    number: 2,
    color: '#3498DB',
    lightColor: '#D4E9F7',
    description: 'Neural Network Structures',
    longDescription: 'The blueprints of intelligence. From the humble feedforward network to the mighty transformer, each architecture represents a different hypothesis about how to structure computation for learning.',
    icon: '⬡',
  },
  models: {
    id: 'models',
    name: 'Models',
    code: 'M',
    number: 3,
    color: '#27AE60',
    lightColor: '#D5F0E0',
    description: 'Model Types by Modality',
    longDescription: 'AI systems categorized by their input/output modalities. This is where most people\'s mental model of AI lives—ChatGPT, DALL-E, Sora. The boundaries between model types are dissolving as we move toward unified multimodal systems.',
    icon: '◈',
  },
  training: {
    id: 'training',
    name: 'Training',
    code: 'T',
    number: 4,
    color: '#E67E22',
    lightColor: '#FDEBD0',
    description: 'Learning Paradigms',
    longDescription: 'The paradigms and methods by which models acquire their capabilities. This is where the magic (and the compute bills) happen. Modern AI training follows a three-stage pipeline: pre-training, fine-tuning, and alignment.',
    icon: '↻',
  },
  techniques: {
    id: 'techniques',
    name: 'Techniques',
    code: 'Tc',
    number: 5,
    color: '#1ABC9C',
    lightColor: '#D0F0ED',
    description: 'Implementation Patterns',
    longDescription: 'The design patterns that bridge theory and practice. These are the methodological choices that turn raw models into useful systems—from prompting strategies to agent architectures.',
    icon: '⚙',
  },
  evaluation: {
    id: 'evaluation',
    name: 'Evaluation',
    code: 'E',
    number: 6,
    color: '#F1C40F',
    lightColor: '#FCF3CF',
    description: 'Metrics & Benchmarks',
    longDescription: 'The metrics and benchmarks by which we assess AI systems. Crucial but often underappreciated—the yardsticks that tell us if we\'re making progress. Benchmarks become obsolete the moment they\'re published.',
    icon: '◉',
  },
  cognition: {
    id: 'cognition',
    name: 'Cognition',
    code: 'C',
    number: 7,
    color: '#E91E63',
    lightColor: '#FCE4EC',
    description: 'Cognitive Capabilities',
    longDescription: 'The mental faculties we associate with intelligence. This is the most philosophically loaded block—these elements represent capabilities we\'re still learning to define, let alone measure.',
    icon: '🧠',
  },
  safety: {
    id: 'safety',
    name: 'Safety',
    code: 'S',
    number: 8,
    color: '#E74C3C',
    lightColor: '#FADBD8',
    description: 'Alignment & Security',
    longDescription: 'Elements devoted to ensuring AI systems remain beneficial, honest, and controllable. This isn\'t paranoia—it\'s engineering prudence. Every powerful technology requires safety measures proportional to its capabilities.',
    icon: '🛡',
  },
  infrastructure: {
    id: 'infrastructure',
    name: 'Infrastructure',
    code: 'I',
    number: 9,
    color: '#607D8B',
    lightColor: '#ECEFF1',
    description: 'Tools & Systems',
    longDescription: 'The tools, platforms, and systems that make AI development and deployment possible. The unglamorous but essential plumbing. Infrastructure determines what\'s possible—breakthroughs here are force multipliers for everything else.',
    icon: '☁',
  },
  data: {
    id: 'data',
    name: 'Data',
    code: 'D',
    number: 10,
    color: '#795548',
    lightColor: '#EFEBE9',
    description: 'Data Types & Processing',
    longDescription: 'Everything related to the information that trains and informs AI systems. Models are only as good as their data—a truth often forgotten in architecture obsession. Data quality often matters more than model architecture.',
    icon: '◫',
  },
  future: {
    id: 'future',
    name: 'Future',
    code: 'X',
    number: 11,
    color: '#FFD700',
    lightColor: '#FFF9E6',
    description: 'AGI, ASI & Speculative',
    longDescription: 'Elements that don\'t yet exist in mature form—concepts that are speculative, theoretical, or early-research-phase. These are marked with distinct styling to indicate their provisional nature. Subject to redefinition as the field evolves.',
    icon: '✧',
    isSpeculative: true,
  },
};

export const BLOCK_ORDER = [
  'foundation',
  'architecture', 
  'models',
  'training',
  'techniques',
  'evaluation',
  'cognition',
  'safety',
  'infrastructure',
  'data',
  'future'
];

export default BLOCKS;
